import { Route, Routes } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { RequireAuth } from "@/components/RequireAuth";
import { RequirePermission } from "@/components/RequirePermission";
import type { Permission } from "@/lib/permissions";
import type { ReactNode } from "react";
import LandingPage from "@/routes/LandingPage";
import LoginPage from "@/routes/login/LoginPage";
import DashboardPage from "@/routes/dashboard/DashboardPage";
import CatalogListPage from "@/routes/catalog/CatalogListPage";
import ProductEditorPage from "@/routes/catalog/ProductEditorPage";
import OrderDetailPage from "@/routes/orders/OrderDetailPage";
import InventoryPage from "@/routes/inventory/InventoryPage";
import AnalyticsPage from "@/routes/analytics/AnalyticsPage";
import OrdersListPage from "@/routes/orders/OrdersListPage";
import UnauthorizedPage from "@/routes/UnauthorizedPage";
import StaffListPage from "@/routes/staff/StaffListPage";
import StaffDetailPage from "@/routes/staff/StaffDetailPage";
import CreateAdminPage from "@/routes/staff/CreateAdminPage";
import SecurityAuditPage from "@/routes/audit/SecurityAuditPage";
import { StubPage } from "@/routes/StubPage";

const guard = (permission: Permission, element: ReactNode) => (
  <RequirePermission permission={permission}>{element}</RequirePermission>
);

export default function App() {
  return (
    <Routes>
      {/* Public marketing landing — signed-in admins are bounced to /dashboard. */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/catalog" element={guard("catalog.manage", <CatalogListPage />)} />
        <Route path="/catalog/new" element={guard("catalog.manage", <ProductEditorPage />)} />
        <Route path="/orders" element={guard("orders.manage", <OrdersListPage />)} />
        <Route path="/orders/:id" element={guard("orders.manage", <OrderDetailPage />)} />
        <Route
          path="/customers"
          element={guard("customers.manage", <StubPage title="Customers" />)}
        />
        <Route path="/inventory" element={guard("inventory.manage", <InventoryPage />)} />
        <Route path="/analytics" element={guard("analytics.view", <AnalyticsPage />)} />
        {/* Staff Registry (Security Audit lands in the next increment). */}
        <Route path="/staff" element={guard("staff.manage", <StaffListPage />)} />
        <Route path="/staff/new" element={guard("staff.manage", <CreateAdminPage />)} />
        <Route path="/staff/:id" element={guard("staff.manage", <StaffDetailPage />)} />
        <Route path="/audit" element={guard("audit.view", <SecurityAuditPage />)} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="*" element={<StubPage title="Not found" />} />
      </Route>
    </Routes>
  );
}
